import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Avatar,
  Stack,
  Badge,
  Button,Container
} from '@mui/material';
import {
  Insights,
  TrendingUp,
  TrendingDown,
  Remove,
  Visibility,
  Phone,
  Language,
  LocationOn,
  CalendarToday,
  Refresh,
  Download,
  ArrowUpward,
  ArrowDownward,
  ShowChart
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ReferenceLine
} from 'recharts';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useBusiness } from '../../contexts/BusinessContext';
import { useBusinessSearch } from '../../contexts/BusinessSearchContext';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import GMBActivity from '../Activity/GmbActivityPage';

const InsightsPage = () => {
  const { locationId: routeLocationId } = useParams();
  const { selectedBusiness } = useBusiness();
  const { minimalBusinesses } = useBusinessSearch();
  
  // Use selected business from context, or fall back to URL parameter
  const locationId = selectedBusiness?.id || selectedBusiness?.locationId || selectedBusiness?.googleLocationId || routeLocationId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [loading, setLoading] = useState(true);
  const [insightsData, setInsightsData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('last_week');
  const [businessInfo, setBusinessInfo] = useState(null);
  const [error, setError] = useState(null);
  const [profileStats, setProfileStats] = useState(null);
  const [visibilityTabIndex, setVisibilityTabIndex] = useState(0); // 0 = Performance, 1 = Activity

  // Fixed three core KPIs
  const coreKPIs = [
    {
      key: 'BUSINESS_IMPRESSIONS',
      label: 'Einbledungen',
      icon: <Visibility />,
      color: amDesignColors.primary,
      description: 'Gesamte Sichtbarkeit in Google (Search + Maps)',
      metrics: ['BUSINESS_IMPRESSIONS_DESKTOP_SEARCH', 'BUSINESS_IMPRESSIONS_MOBILE_SEARCH', 'BUSINESS_IMPRESSIONS_DESKTOP_MAPS', 'BUSINESS_IMPRESSIONS_MOBILE_MAPS']
    },
    {
      key: 'CALL_CLICKS',
      label: 'Anrufe',
      icon: <Phone />,
      color: amDesignColors.success,
      description: 'Anzahl der Anrufe über Google',
      metrics: ['CALL_CLICKS']
    },
    {
      key: 'WEBSITE_CLICKS',
      label: 'Webseiten-Besucher',
      icon: <Language />,
      color: amDesignColors.warning,
      description: 'Anzahl der Website-Besuche über Google',
      metrics: ['WEBSITE_CLICKS']
    }
  ];

  // Comprehensive time period options
  const periodOptions = [
    // {
    //   value: 'today',
    //   label: 'Heute',
    //   getDates: () => {
    //     const today = new Date();
    //     return { start: today, end: today };
    //   }
    // },
    // {
    //   value: 'yesterday',
    //   label: 'Gestern',
    //   getDates: () => {
    //     const yesterday = new Date();
    //     yesterday.setDate(yesterday.getDate() - 1);
    //     return { start: yesterday, end: yesterday };
    //   }
    // },
    {
      value: 'last_week',
      label: 'Letzte 7 Tage',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 7);
        return { start, end };
      }
    },
    // {
    //   value: 'last_30_days',
    //   label: 'Letzte 30 Tage',
    //   getDates: () => {
    //     const end = new Date();
    //     const start = new Date();
    //     start.setDate(start.getDate() - 30);
    //     return { start, end };
    //   }
    // },
    {
      value: 'last_month',
      label: 'Letzte Monat',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        return { start, end };
      }
    },
    {
      value: 'last_3_months',
      label: 'Letzte 3 Monate',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 3);
        return { start, end };
      }
    }
    // {
    //   value: 'last_6_months',
    //   label: 'Letzte 6 Monate',
    //   getDates: () => {
    //     const end = new Date();
    //     const start = new Date();
    //     start.setMonth(start.getMonth() - 6);
    //     return { start, end };
    //   }
    // },
    // {
    //   value: 'last_year',
    //   label: 'Letztes Jahr',
    //   getDates: () => {
    //     const end = new Date();
    //     const start = new Date();
    //     start.setFullYear(start.getFullYear() - 1);
    //     return { start, end };
    //   }
    // }
  ];

  // Load business info and insights data when locationId changes
  useEffect(() => {
    console.log('🔄 Location ID changed:', locationId);
    if (locationId && locationId !== 'undefined') {
      loadBusinessInfo();
      loadInsightsData();
      loadProfileStats();
    }
  }, [locationId]);

  // React to selected business changes from dropdown
  useEffect(() => {
    if (selectedBusiness) {
      console.log('🔄 Selected business changed:', selectedBusiness.businessName);
      const newLocationId = selectedBusiness.id || selectedBusiness.locationId || selectedBusiness.googleLocationId;
      if (newLocationId && newLocationId !== locationId) {
        console.log('📍 Updating insights for new business:', newLocationId);
        // The locationId will update and trigger the first useEffect
      }
    }
  }, [selectedBusiness]);

  // Reload data when period changes
  useEffect(() => {
    if (locationId && locationId !== 'undefined') {
      console.log('📅 Period changed, reloading data:', selectedPeriod);
      loadInsightsData();
      loadProfileStats();
    }
  }, [selectedPeriod]);

  const loadBusinessInfo = async () => {
    try {
      const response = await axios.get(`/api/businesses/${locationId}/ranking-overview`);

      console.log('---bisines info api',response.data)
      if (response.data.success) {
        setBusinessInfo({
          businessName: response.data.data.businessName,
          location: response.data.location,
          googleLocationId: locationId
        });
      }
    } catch (error) {
      console.error('Error loading business info:', error);
    }
  };

  const loadProfileStats = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.log('⚠️ No token found for profile stats');
        return;
      }

      const periodConfig = periodOptions.find(p => p.value === selectedPeriod);
      const { start: startDate, end: endDate } = periodConfig.getDates();

      const startDateObj = {
        year: startDate.getFullYear(),
        month: startDate.getMonth() + 1,
        day: startDate.getDate()
      };

      const endDateObj = {
        year: endDate.getFullYear(),
        month: endDate.getMonth() + 1,
        day: endDate.getDate()
      };

      console.log('📊 Loading profile stats for:', locationId, startDateObj, endDateObj);

      const response = await axios.get(`/api/businesses/${locationId}/profile-stats`, {
        params: {
          startDate: JSON.stringify(startDateObj),
          endDate: JSON.stringify(endDateObj)
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📊 Profile Stats API response:', response.data);

      if (response.data.success && response.data.data) {
        setProfileStats(response.data.data);
        console.log('✅ Profile Stats loaded:', response.data.data);
      } else {
        console.log('⚠️ Profile Stats API returned unexpected format:', response.data);
        setProfileStats(null);
      }
    } catch (error) {
      console.error('❌ Error loading profile stats:', error.response?.status, error.response?.data || error.message);
      setProfileStats(null);
    }
  };

  const loadInsightsData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is authenticated
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Authentifizierung erforderlich. Bitte melden Sie sich an.');
        setLoading(false);
        return;
      }

      // Check localStorage cache (4 hours TTL) if not forcing refresh
      const cacheKey = `insightsData_${locationId}_${selectedPeriod}`;
      if (!forceRefresh) {
        const cached = localStorage.getItem(cacheKey);
        const cachedTime = localStorage.getItem(`${cacheKey}_time`);
        const FOUR_HOURS = 4 * 60 * 60 * 1000;

        if (cached && cachedTime && Date.now() - parseInt(cachedTime) < FOUR_HOURS) {
          setInsightsData(JSON.parse(cached));
          setLoading(false);
          return;
        }
      } else {
        // Clear cache when forcing refresh
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_time`);
      }

      // Get date range based on selected period
      const periodConfig = periodOptions.find(p => p.value === selectedPeriod);
      const { start: startDate, end: endDate } = periodConfig.getDates();

      // Get all metrics needed for the three KPIs
      const allMetrics = coreKPIs.reduce((acc, kpi) => [...acc, ...kpi.metrics], []);

      console.log('📊 Loading insights data:', {
        locationId,
        period: selectedPeriod,
        dateRange: `${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`,
        metrics: allMetrics
      });

      const Start_Date={
            year: startDate.getFullYear(),
            month: startDate.getMonth() + 1,
            day: startDate.getDate()
          }


        const End_Date=  {
            year: endDate.getFullYear(),
            month: endDate.getMonth() + 1,
            day: endDate.getDate()
          }



      const response = await axios.post(`/api/businesses/${locationId}/insights`, {
        metrics: allMetrics,
        dateRange: {
          startDate:Start_Date ,
          endDate: End_Date
        }
      });

      if (response.data.success) {
        setInsightsData(response.data.data);
        // Cache in localStorage for 4 hours (with fallback if quota exceeded)
        try {
          localStorage.setItem(cacheKey, JSON.stringify(response.data.data));
          localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
        } catch (storageError) {
          if (storageError.name === 'QuotaExceededError') {
            console.warn('⚠️ localStorage quota exceeded. Skipping cache for this dataset.');
            // Clear old caches to free up space
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith('insightsData_')) {
                localStorage.removeItem(key);
                localStorage.removeItem(`${key}_time`);
              }
            }
          } else {
            console.error('⚠️ Error caching insights data:', storageError);
          }
        }
        console.log('------insigts',response.data.data)
        console.log('✅ Insights data loaded successfully');
      } else {
        setError(response.data.error || 'Failed to load insights data');
      }

    } catch (error) {
      console.error('Error loading insights:', error);
      
      if (error.response?.status === 401) {
        // Don't redirect for API 401 errors - just show the error
        setError('Google API-Authentifizierung fehlgeschlagen. Demo-Daten werden angezeigt.');
        console.log('ℹ️ Using fallback for 401 Google API error');
      } else if (error.response?.status === 403) {
        setError('Keine Berechtigung für Insights-Daten. Kontaktieren Sie Ihren Administrator.');
      } else if (error.response?.status === 404) {
        setError('Business-Profil nicht gefunden.');
      } else {
        setError(error.response?.data?.error || error.message || 'Fehler beim Laden der Insights-Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  // Process data for the three core KPIs
  const processKPIData = () => {
    if (!insightsData || !insightsData.timeSeries) {
      console.log('⚠️ No insights data or timeSeries available');
      return [];
    }

    console.log('📊 Processing KPI data:', {
      timeSeriesKeys: Object.keys(insightsData.timeSeries),
      sampleMetric: Object.keys(insightsData.timeSeries)[0],
      sampleData: insightsData.timeSeries[Object.keys(insightsData.timeSeries)[0]]?.slice(0, 2)
    });

    const dateMap = new Map();

    // Process time series data and aggregate for KPIs
    Object.keys(insightsData.timeSeries).forEach(metric => {
      const metricData = insightsData.timeSeries[metric] || [];
      console.log(`📈 Processing metric ${metric}: ${metricData.length} data points`);
      
      metricData.forEach((dataPoint, index) => {
        const dateKey = `${dataPoint.date.year}-${String(dataPoint.date.month).padStart(2, '0')}-${String(dataPoint.date.day).padStart(2, '0')}`;
        
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            date: dateKey,
            formattedDate: new Date(dataPoint.date.year, dataPoint.date.month - 1, dataPoint.date.day).toLocaleDateString('de-DE'),
            BUSINESS_IMPRESSIONS: 0,
            CALL_CLICKS: 0,
            WEBSITE_CLICKS: 0
          });
        }
        
        const entry = dateMap.get(dateKey);
        const value = dataPoint.value || 0;
        
        // Log each data point for debugging
        if (index < 3) { // Log first 3 entries for each metric
          console.log(`   ${dateKey}: ${metric} = ${value}`);
        }
        
        // Aggregate business impressions from all sources
        if (metric.includes('BUSINESS_IMPRESSIONS')) {
          entry.BUSINESS_IMPRESSIONS += value;
        } else if (metric === 'CALL_CLICKS') {
          entry.CALL_CLICKS += value; // Use += in case there are multiple call click metrics
        } else if (metric === 'WEBSITE_CLICKS') {
          entry.WEBSITE_CLICKS += value; // Use += in case there are multiple website click metrics
        }
      });
    });

    const processedData = Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
    
    console.log('📊 Processed KPI data:', {
      totalDays: processedData.length,
      sampleDay: processedData[0],
      totalImpressions: processedData.reduce((sum, day) => sum + day.BUSINESS_IMPRESSIONS, 0),
      totalCalls: processedData.reduce((sum, day) => sum + day.CALL_CLICKS, 0),
      totalWebsite: processedData.reduce((sum, day) => sum + day.WEBSITE_CLICKS, 0)
    });

    return processedData;
  };

  // Calculate KPI totals and before/after figures
  const calculateKPITotals = () => {
    if (!insightsData || !insightsData.timeSeries) {
      console.log('⚠️ No data for KPI totals calculation');
      return {};
    }

    const kpiTotals = {};
    const kpiBeforeAfter = {};
    
    // Get business creation date as reference point
    const businessCreatedAt = insightsData?.businessInfo?.createdAt;
    const referenceDate = businessCreatedAt ? new Date(businessCreatedAt) : null;
    
    console.log('🔢 Calculating KPI totals and before/after figures...');
    console.log('📅 Reference date (business creation):', referenceDate?.toLocaleDateString('de-DE'));
    
    coreKPIs.forEach(kpi => {
      let total = 0;
      let beforeTotal = 0;
      let afterTotal = 0;
      let beforeDays = 0;
      let afterDays = 0;
      
      console.log(`📊 Calculating total for ${kpi.key}:`);
      
      kpi.metrics.forEach(metric => {
        const metricData = insightsData.timeSeries[metric] || [];
        
        metricData.forEach(dataPoint => {
          const pointDate = new Date(dataPoint.date.year, dataPoint.date.month - 1, dataPoint.date.day);
          const value = dataPoint.value || 0;
          total += value;
          
          // Split into before/after reference date
          if (referenceDate) {
            if (pointDate < referenceDate) {
              beforeTotal += value;
              if (value > 0) beforeDays++;
            } else {
              afterTotal += value;  
              if (value > 0) afterDays++;
            }
          }
        });
        
        const metricTotal = metricData.reduce((sum, dataPoint) => sum + (dataPoint.value || 0), 0);
        console.log(`   ${metric}: ${metricTotal} (from ${metricData.length} data points)`);
      });
      
      kpiTotals[kpi.key] = total;
      
      // Calculate averages per day for before/after periods
      kpiBeforeAfter[kpi.key] = {
        beforeTotal,
        afterTotal,
        beforeAvgPerDay: beforeDays > 0 ? (beforeTotal / beforeDays) : 0,
        afterAvgPerDay: afterDays > 0 ? (afterTotal / afterDays) : 0,
        beforeDays,
        afterDays
      };
      
      console.log(`✅ ${kpi.key} - Total: ${total}, Before: ${beforeTotal}/${beforeDays} days (${kpiBeforeAfter[kpi.key].beforeAvgPerDay.toFixed(1)}/day), After: ${afterTotal}/${afterDays} days (${kpiBeforeAfter[kpi.key].afterAvgPerDay.toFixed(1)}/day)`);
    });

    console.log('📊 Final KPI totals:', kpiTotals);
    return { totals: kpiTotals, beforeAfter: kpiBeforeAfter };
  };

  // Calculate trend for KPIs
  const calculateTrend = (kpiKey, chartData) => {
    if (chartData.length < 2) return null;

    const recentData = chartData.slice(-3); // Last 3 days
    const olderData = chartData.slice(-6, -3); // Previous 3 days

    if (recentData.length === 0 || olderData.length === 0) return null;

    const recentAvg = recentData.reduce((sum, day) => sum + (day[kpiKey] || 0), 0) / recentData.length;
    const olderAvg = olderData.reduce((sum, day) => sum + (day[kpiKey] || 0), 0) / olderData.length;

    if (olderAvg === 0) return null;

    return ((recentAvg - olderAvg) / olderAvg) * 100;
  };

  // Show loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Lade Insights-Daten{selectedBusiness?.businessName ? ` für ${selectedBusiness.businessName}` : ''}...
        </Typography>
      </Box>
    );
  }

  // Show message if no business selected
  if (!locationId || locationId === 'undefined') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center">
          <Insights sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" color="textSecondary" gutterBottom>
            Kein Business ausgewählt
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Bitte wählen Sie ein Business aus der Dropdown-Liste aus, um Insights-Daten anzuzeigen.
          </Typography>
        </Box>
      </Box>
    );
  }

  const chartData = processKPIData();
  const kpiData = calculateKPITotals();
  const kpiTotals = kpiData.totals || {};
  const kpiBeforeAfter = kpiData.beforeAfter || {};
  
  // Get business creation date for reference line
  const businessCreatedAt = insightsData?.businessInfo?.createdAt;
  const creationDateForChart = businessCreatedAt ? new Date(businessCreatedAt).toLocaleDateString('de-DE') : null;
  return (

    <Container maxWidth="xl" sx={{ py: 1 }}>
   
      {/* Top-Level Tabs: Performance / Activity */}
      <Box sx={{ mb: 2, borderBottom: `2px solid ${amDesignColors.border}` }}>
        <Box sx={{ display: 'flex', gap: 0 }}>
          <Button
            onClick={() => setVisibilityTabIndex(0)}
            sx={{
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: visibilityTabIndex === 0 ? 600 : 500,
              color: visibilityTabIndex === 0 ? amDesignColors.primary : amDesignColors.text.secondary,
              borderBottom: visibilityTabIndex === 0 ? `3px solid ${amDesignColors.primary}` : 'none',
              borderRadius: 0,
              paddingBottom: 1.5,
              marginBottom: '-2px'
            }}
          >
            Performance
          </Button>
          <Button
            onClick={() => setVisibilityTabIndex(1)}
            sx={{
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: visibilityTabIndex === 1 ? 600 : 500,
              color: visibilityTabIndex === 1 ? amDesignColors.primary : amDesignColors.text.secondary,
              borderBottom: visibilityTabIndex === 1 ? `3px solid ${amDesignColors.primary}` : 'none',
              borderRadius: 0,
              paddingBottom: 1.5,
              marginBottom: '-2px',
              marginLeft: 2
            }}
          >
            Activity
          </Button>
        </Box>
      </Box>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
        <Box>
          <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: amDesignColors.primary,
                fontSize: '0.75rem'
              }}
          >
            Geschäftseinblicke
          </Typography>
          <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                fontWeight: 800,
                letterSpacing: '-0.025em',
                fontSize: '2rem'
              }}
           gutterBottom >
            Wie sich die Sichtbarkeit entwickelt
          </Typography>

           <Typography
                variant="body2"
                sx={{
                  color: amDesignColors.text.secondary,
                  fontFamily: amDesignTypography.fontFamily.body
                }}
              >
                {selectedBusiness?.businessName || businessInfo?.businessName || 'Business'}
            </Typography>
        </Box>

        {/* Right side: Period Selector + Action Buttons (only show period selector on Performance tab) */}
        <Box display="flex" gap={2} alignItems="center">
          {/* Period Selector - Only visible on Performance tab */}
          {visibilityTabIndex === 0 && (
            <Box
              sx={{
                display: 'inline-flex',
                backgroundColor: '#F0F0F0',
                borderRadius: '8px',
                padding: '4px',
                gap: 0.5
              }}
            >
              {periodOptions.map(option => (
                <Button
                  key={option.value}
                  onClick={() => setSelectedPeriod(option.value)}
                  sx={{
                    px: 2,
                    py: 0.75,
                    fontSize: '0.85rem',
                    fontWeight: selectedPeriod === option.value ? 600 : 500,
                    borderRadius: '6px',
                    textTransform: 'none',
                    backgroundColor: selectedPeriod === option.value ? amDesignColors.white : 'transparent',
                    color: selectedPeriod === option.value ? amDesignColors.text.primary : amDesignColors.text.secondary,
                    border: 'none',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: selectedPeriod === option.value ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    '&:hover': {
                      backgroundColor: selectedPeriod === option.value ? amDesignColors.white : 'rgba(255,255,255,0.5)',
                    }
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Action Buttons */}
          <Box display="flex" gap={1}>
            <Tooltip title="Daten aktualisieren">
              <IconButton onClick={() => loadInsightsData(true)} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Daten exportieren">
              <IconButton disabled={!insightsData}>
                <Download />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Performance Tab Content */}
      {visibilityTabIndex === 0 && (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

      {/* Debug Data Display
      {insightsData && process.env.NODE_ENV === 'development' && (
        <Card sx={{ mb: 3, backgroundColor: amDesignColors.paper }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔍 Debug: API Response Data
            </Typography>
            <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
              {JSON.stringify(insightsData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      */}

      {insightsData && (
        <>
          {/* Enhanced KPI Dashboard */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {coreKPIs.map((kpi, index) => {
              const total = kpiTotals[kpi.key] || 0;
              const average = chartData.length > 0 ? (total / chartData.length).toFixed(1) : 0;
              const trend = calculateTrend(kpi.key, chartData);
              const beforeAfterData = kpiBeforeAfter[kpi.key];

              // Calculate performance improvement percentage
              let improvementPercentage = 0;
              let hasImprovement = false;
              if (beforeAfterData) {
                if (beforeAfterData.beforeAvgPerDay > 0) {
                  improvementPercentage = ((beforeAfterData.afterAvgPerDay - beforeAfterData.beforeAvgPerDay) / beforeAfterData.beforeAvgPerDay * 100);
                  hasImprovement = Math.abs(improvementPercentage) > 0.1;
                } else if (beforeAfterData.beforeAvgPerDay === 0 && beforeAfterData.afterAvgPerDay > 0) {
                  // When starting from 0, any positive value is improvement
                  improvementPercentage = 1;
                  hasImprovement = true;
                } else if (beforeAfterData.afterAvgPerDay === 0 && beforeAfterData.beforeAvgPerDay > 0) {
                  // When going to 0 from positive, it's a decline
                  improvementPercentage = -1;
                  hasImprovement = true;
                }
              }

              return (
                <Grid item xs={12} md={4} key={kpi.key}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      background: 'white',
                      borderRadius: '18px',
                      border: 'none',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Icon Chip with Colored Background */}
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          backgroundColor: `${kpi.color}15`,
                          mb: 2,
                          '& svg': {
                            fontSize: 28,
                            color: kpi.color
                          }
                        }}
                      >
                        {kpi.icon}
                      </Box>

                      {/* Title and Description */}
                      <Box mb={2.5}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="700"
                          color="text.primary"
                          sx={{ lineHeight: 1.2, mb: 0.5 }}
                        >
                          {kpi.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.85rem', lineHeight: 1.3 }}
                        >
                          {kpi.description}
                        </Typography>
                      </Box>

                      {/* Main Metric Display */}
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="h3"
                          component="div"
                          fontWeight="800"
                          sx={{
                            color: '#000000',
                            fontSize: '2.5rem',
                            fontFamily: '"JetBrains Mono", monospace',
                            lineHeight: 1,
                            mb: 1.5
                          }}
                        >
                          {total.toLocaleString()}
                        </Typography>

                        {/* Bottom row: Average + Before/After */}
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            Ø {average}/Tag
                          </Typography>

                          {/* Before/After Comparison - Colored Pill Badge */}
                          {beforeAfterData && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.6,
                                px: 1,
                                py: 0.4,
                                borderRadius: 1,
                                backgroundColor: improvementPercentage > 0 ? '#E8F5E9' : improvementPercentage < 0 ? '#FFEBEE' : '#F5F5F5',
                              }}
                            >
                              {improvementPercentage > 0 ? (
                                <TrendingUp sx={{ color: '#00897B', fontSize: 15, flexShrink: 0 }} />
                              ) : improvementPercentage < 0 ? (
                                <TrendingDown sx={{ color: '#D32F2F', fontSize: 15, flexShrink: 0 }} />
                              ) : (
                                <Remove sx={{ color: '#999999', fontSize: 15, flexShrink: 0 }} />
                              )}
                              <Typography
                                variant="body2"
                                fontWeight="500"
                                sx={{
                                  color: improvementPercentage > 0 ? '#00897B' : improvementPercentage < 0 ? '#D32F2F' : '#666666',
                                  fontSize: '0.85rem'
                                }}
                              >
                                {Math.round(beforeAfterData.beforeAvgPerDay)} → {Math.round(beforeAfterData.afterAvgPerDay)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Time Series Chart for Three KPIs */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  KPI-Zeitverlauf ({periodOptions.find(p => p.value === selectedPeriod)?.label})
                </Typography>
                {businessCreatedAt && (
                  <Chip
                    icon={<CalendarToday />}
                    label={`Unternehmen hinzugefügt: ${new Date(businessCreatedAt).toLocaleDateString('de-DE')}`}
                    variant="outlined"
                    size="small"
                    sx={{ color: '#e91e63', borderColor: '#e91e63' }}
                  />
                )}
              </Box>
              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="formattedDate"
                      tick={{ fontSize: 12 }}
                      interval={chartData.length > 7 ? Math.floor(chartData.length / 7) : 0}
                    />
                    <YAxis />
                    <RechartsTooltip
                      labelFormatter={(label) => {
                        let tooltip = `Datum: ${label}`;
                        if (label === creationDateForChart) {
                          tooltip += ' (Unternehmen hinzugefügt)';
                        }
                        return tooltip;
                      }}
                      formatter={(value, name) => {
                        const kpi = coreKPIs.find(k => k.key === name);
                        return [value?.toLocaleString() || 0, kpi?.label || name];
                      }}
                    />
                    <Legend />

                    {/* Business Creation Date Reference Line */}
                    {creationDateForChart && (
                      <ReferenceLine
                        x={creationDateForChart}
                        stroke="#e91e1eff"
                        strokeDasharray="4 5"
                        strokeWidth={4}
                        label={{
                          value: "Unternehmen hinzugefügt",
                          position: "top",
                          style: {
                            fontSize: 1,
                            fontWeight: 'bold',
                            fill: '#e91e63'
                          }
                        }}
                      />
                    )}

                    {coreKPIs.map(kpi => (
                      <Line
                        key={kpi.key}
                        type="monotone"
                        dataKey={kpi.key}
                        stroke={kpi.color}
                        strokeWidth={3}
                        dot={{ fill: kpi.color, strokeWidth: 2, r: 5 }}
                        name={kpi.label}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          {/* Business Profile Stats Section */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', justifyContent: 'space-between' }}>
            {/* Stats Cards Grid */}
            <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
              {(() => {
                const stats = [
                  { label: 'Bewertungen', value: profileStats?.reviews ?? 0, color: '#FF9500' },
                  { label: 'Medien', value: profileStats?.photos ?? 0, color: '#2196F3' },
                  { label: 'Posts', value: profileStats?.posts ?? 0, color: '#4CAF50' },
                  { label: 'Keywords', value: profileStats?.keywords ?? 0, color: '#E91E63' },
                  { label: 'Services', value: profileStats?.services ?? 0, color: '#9E9E9E' }
                ];

                return stats.map((stat) => (
                  <Card
                    key={stat.label}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'center',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                      borderRadius: '12px',
                      border: '1px solid #F0F0F0',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    <CardContent sx={{ py: 5, px: 1, textAlign: 'center' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stat.color, mx: 'auto', mb: 1 }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25, fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem' }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                        {stat.label}
                      </Typography>
                    </CardContent>
                  </Card>
                ));
              })()}
            </Box>

            {/* Onboarding Progress Card */}
            {businessCreatedAt && (() => {
              const creationDate = new Date(businessCreatedAt);
              const today = new Date();
              const daysSinceCreation = Math.floor((today - creationDate) / (1000 * 60 * 60 * 24));
              const weeks = Math.ceil(daysSinceCreation / 7);

              // Get initial KPI values (before period average)
              const kpiBeforeAfter = kpiData.beforeAfter || {};
              const impressionsBefore = kpiBeforeAfter['BUSINESS_IMPRESSIONS']?.beforeAvgPerDay || 0;
              const impressionsAfter = kpiBeforeAfter['BUSINESS_IMPRESSIONS']?.afterAvgPerDay || 0;

              return (
                <Card
                  sx={{
                    minWidth: '280px',
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #8B1A28 0%, #6B1220 100%)',
                    color: 'white',
                    borderRadius: '18px',
                    boxShadow: '0 8px 24px rgba(107, 18, 32, 0.4)'
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        opacity: 0.95,
                        display: 'block',
                        mb: 1
                      }}
                    >
                      SEIT BETREUUNG DURCH A&M
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.6, fontSize: '0.9rem', opacity: 0.95 }}>
                      Onboarding am {creationDate.toLocaleDateString('de-DE')}. In rund {weeks} {weeks === 1 ? 'Woche' : 'Wochen'} von 0 auf messbare Sichtbarkeit.
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 3 }}>
                      {/* Vorher */}
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.8rem', display: 'block', mb: 0.5 }}>
                          Vorher
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', fontSize: '1.25rem' }}>
                          {impressionsBefore.toFixed(1)}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                          /Tag
                        </Typography>
                      </Box>

                      {/* Arrow */}
                      <Box sx={{ mx: 2 }}>
                        <Typography variant="h6" sx={{ opacity: 0.9 }}>→</Typography>
                      </Box>

                      {/* Nachher */}
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.8rem', display: 'block', mb: 0.5 }}>
                          Nachher
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', fontSize: '1.25rem' }}>
                          {impressionsAfter.toFixed(1)}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                          /Tag
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })()}

          </Box>


        </>
      )}

          {!insightsData && !loading && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Insights sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h5" color="textSecondary" gutterBottom>
                  Keine Insights-Daten verfügbar
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Für diese Business-Location sind keine Performance-Daten verfügbar.
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Activity Tab Content */}
      {visibilityTabIndex === 1 && (
        <GMBActivity />
      )}
   
    </Container>
  );
};

export default InsightsPage;